/**
 * Creates a wrapper that executes a given function with a specified delay and allows for cancellation.
 *
 * @param fn The function to be executed after the delay.
 * @param delay The delay in milliseconds before the function is executed.
 * @returns An object containing:
 *   - `schedule`: A function that, when called, schedules `fn` to be executed after `delay` milliseconds.
 *   - `cancel`: A function that cancels the scheduled execution of `fn` if it has not yet executed.
 */
export function createDelayedExecution<T extends any[]>(fn: (...args: T) => void, delay: number): 
{ schedule: (...args: T) => void; cancel: () => void } {
  
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const schedule = (...args: T) => {
    cancel(); // Cancel any previously scheduled execution
    timeoutId = setTimeout(() => fn(...args), delay);
  };

  const cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return { schedule, cancel };
}